import React from "react";
import { Grid, Typography, Button, Divider, Box } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import intl from "react-intl-universal";
import { makeStyles } from "@material-ui/core/styles";
import { useMutate } from "restful-react";
import { useSnackbar } from "notistack";

import { SelectField } from "components/SelectField";
import {
  getRoleOptions,
  isTeamManager,
  buildNumberOptions,
  FIELD_NAME_TEAM_MANAGER,
  FIELD_NAME_MATCH_ADMIN,
  FIELD_NAME_NORMAL_USER,
  ROLE_TEAM_MANAGER_VALUE,
  ROLE_MATCH_ADMIN_VALUE,
  ROLE_NORMAL_VALUE,
} from "utils";
import { Checkbox } from "components/Checkbox";
import {
  IUpdatePlayerNumberInput,
  IUpdateTeamPlayerRole,
  updatePlayerNumber,
  updatePlayerRoles,
} from "apis";

import { IEditPlayerProps } from "./types";

const FIELD_NUMBER = "number";

const useStyles = makeStyles((theme) => ({
  grid: {
    display: "flex",
    alignSelf: "center",
  },
  text: {
    margin: "revert",
    marginRight: theme.spacing(2),
  },
}));

export const EditPlayer = ({
  teamId,
  player,
  disableNumber,
  viewerRoles = [],
  onRemove,
  onUpdate,
}: IEditPlayerProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: mutateUpdatePlayerNumber } = useMutate({
    verb: "POST",
    path: updatePlayerNumber(teamId, player.id),
  });

  const { mutate: mutateUpdatePlayerRoles } = useMutate({
    verb: "POST",
    path: updatePlayerRoles(teamId, player.id),
  });

  const numberOptions = buildNumberOptions();
  const roleOptions = getRoleOptions(viewerRoles);

  const handleRemoveClick = () => {
    onRemove(player.id);
  };

  const initialValues = {
    [FIELD_NUMBER]: player.number,
    [FIELD_NAME_TEAM_MANAGER]: player.roles.includes(ROLE_TEAM_MANAGER_VALUE),
    [FIELD_NAME_MATCH_ADMIN]: player.roles.includes(ROLE_MATCH_ADMIN_VALUE),
    [FIELD_NAME_NORMAL_USER]: player.roles.includes(ROLE_NORMAL_VALUE),
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          const playerNumberInput: IUpdatePlayerNumberInput = {
            playerNum: values[FIELD_NUMBER],
          };

          const roles = [];
          if (values[FIELD_NAME_TEAM_MANAGER]) {
            roles.push(ROLE_TEAM_MANAGER_VALUE);
          }
          if (values[FIELD_NAME_MATCH_ADMIN]) {
            roles.push(ROLE_MATCH_ADMIN_VALUE);
          }
          if (values[FIELD_NAME_NORMAL_USER]) {
            roles.push(ROLE_NORMAL_VALUE);
          }
          const rolesInput: IUpdateTeamPlayerRole = {
            roles,
          };

          Promise.all([
            mutateUpdatePlayerNumber(playerNumberInput),
            mutateUpdatePlayerRoles(rolesInput),
          ]).then((values) => {
            setSubmitting(true);
            const [updateNumberRes, updateRolesRes] = values;
            if (updateNumberRes && updateRolesRes) {
              enqueueSnackbar(intl.get("PLAYER_EDIT_SUCCESS"), {
                variant: "success",
              });
              onUpdate();
              return;
            }

            if (updateNumberRes || updateRolesRes) {
              onUpdate();
            }

            if (!updateNumberRes) {
              enqueueSnackbar(intl.get("PLAYER_EDIT_NUMBER_FAILED"), {
                variant: "error",
              });
            }

            if (!updateRolesRes) {
              enqueueSnackbar(intl.get("PLAYER_EDIT_ROLES_FAILED"), {
                variant: "error",
              });
            }
          });
        }}
      >
        {({ dirty }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={6} lg={2} className={classes.grid}>
                <Typography>{player.name}</Typography>
              </Grid>
              <Grid item xs={6} lg={3} className={classes.grid}>
                <Typography className={classes.text}>
                  {intl.get("PLAYER_EDIT_NUMBER")}
                </Typography>
                <Field
                  component={SelectField}
                  name={FIELD_NUMBER}
                  options={numberOptions}
                  disabled={disableNumber}
                />
              </Grid>
              <Grid item xs={12} lg={5} className={classes.grid}>
                <Typography className={classes.text}>
                  {intl.get("PLAYER_EDIT_ROLE")}
                </Typography>
                {roleOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    name={option.name}
                    inFormik
                    label={option.label}
                    disabled={option.disabled}
                  />
                ))}
              </Grid>
              <Grid item xs={12} lg={2} className={classes.grid}>
                <Button
                  type="submit"
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={!dirty}
                >
                  {intl.get("PLAYER_EDIT_SAVE")}
                </Button>
                <Box display="inline" ml={2} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRemoveClick}
                  disabled={isTeamManager(player.roles)}
                >
                  {intl.get("PLAYER_EDIT_REMOVE")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <Divider />
    </>
  );
};
